import { supabase } from '../lib/supabase';
import { Company, CompanyMember, CompanyJob, CompanyForm, JobForm } from '../types';

export class CompanyService {
  // Test database connection
  static async testConnection(): Promise<boolean> {
    try {
      console.log('🔌 Testing database connection...');
      const { data, error } = await supabase
        .from('companies')
        .select('count')
        .limit(1);
      
      if (error) {
        console.error('❌ Database connection test failed:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return false;
      }
      
      console.log('✅ Database connection successful');
      return true;
    } catch (error) {
      console.error('💥 Database connection test error:', error);
      return false;
    }
  }

  // Debug function to check what companies exist
  static async debugCompanies(): Promise<void> {
    try {
      console.log('🔍 Debugging companies table...');
      
      // Check if table exists by trying to select from it
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, slug, is_active')
        .limit(10);
      
      if (error) {
        console.error('❌ Error accessing companies table:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return;
      }
      
      console.log('📊 Companies in database:', {
        count: data?.length || 0,
        companies: data?.map(c => ({ id: c.id, name: c.name, slug: c.slug, is_active: c.is_active })) || []
      });
      
    } catch (error) {
      console.error('💥 Debug companies error:', error);
    }
  }

  // Company CRUD operations
  static async getCompanies(filters?: {
    industry?: string;
    companySize?: string;
    companyType?: string;
    isHiring?: boolean;
    isRemoteFriendly?: boolean;
    location?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    let query = supabase
      .from('companies')
      .select('*')
      .eq('is_active', true);

    if (filters?.industry) {
      query = query.eq('industry', filters.industry);
    }

    if (filters?.companySize) {
      query = query.eq('company_size', filters.companySize);
    }

    if (filters?.companyType) {
      query = query.eq('company_type', filters.companyType);
    }

    if (filters?.isHiring !== undefined) {
      query = query.eq('is_hiring', filters.isHiring);
    }

    if (filters?.isRemoteFriendly !== undefined) {
      query = query.eq('is_remote_friendly', filters.isRemoteFriendly);
    }

    if (filters?.location) {
      query = query.or(`headquarters_city.ilike.%${filters.location}%,headquarters_country.ilike.%${filters.location}%`);
    }

    if (filters?.search) {
      query = query.textSearch('name,description,tagline', filters.search);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, (filters.offset + (filters.limit || 10)) - 1);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data as Company[];
  }

  static async getCompanyById(id: string): Promise<Company | null> {
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .eq('id', id)
      .eq('is_active', true)
      .single();

    if (error) throw error;
    return data as Company;
  }

  static async getCompanyBySlug(slug: string): Promise<Company | null> {
    try {
      console.log('🔍 Fetching company by slug:', slug);
      
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('❌ Error fetching company by slug:', {
          slug,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });

        // Handle "not found" case specifically
        if (error.code === 'PGRST116') { // No rows returned
          console.log('ℹ️ No company found with slug:', slug);
          return null;
        }

        // For other errors, throw them
        throw new Error(`Failed to fetch company: ${error.message}`);
      }

      console.log('✅ Company found:', { id: data.id, name: data.name, slug: data.slug });
      
      // Ensure proper field mapping from database to TypeScript interface
      const mappedCompany = {
        ...data,
        profileViews: data.profile_views || 0,
        jobViews: data.job_views || 0,
        applicationCount: data.application_count || 0,
        isVerified: data.is_verified || false,
        isHiring: data.is_hiring || false,
        isActive: data.is_active !== false,
        companySize: data.company_size,
        companyType: data.company_type,
        website: data.website,
        impressumUrl: (data as any).impressum_url,
        email: data.email,
        phone: data.phone,
        linkedinUrl: data.linkedin_url,
        twitterUrl: data.twitter_url,
        facebookUrl: data.facebook_url,
        instagramUrl: data.instagram_url,
        headquartersAddress: data.headquarters_address,
        headquartersCity: data.headquarters_city,
        headquartersState: data.headquarters_state,
        headquartersCountry: data.headquarters_country,
        headquartersPostalCode: data.headquarters_postal_code,
        isRemoteFriendly: data.is_remote_friendly || false,
        remotePolicy: data.remote_policy,
        foundedYear: data.founded_year,
        revenueRange: data.revenue_range,
        employeeCount: data.employee_count,
        missionStatement: data.mission_statement,
        values: data.values || [],
        benefits: data.benefits || [],
        perks: data.perks || [],
        logoUrl: data.logo_url,
        coverImageUrl: data.cover_image_url,
        brandColors: {
          primary: data.brand_primary_color,
          secondary: data.brand_secondary_color
        },
        memberIds: data.member_ids || [],
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
      
      return mappedCompany as Company;
    } catch (error) {
      console.error('💥 getCompanyBySlug failed:', error);
      
      if (error instanceof Error) {
        throw new Error(`Failed to load company data: ${error.message}`);
      } else {
        throw new Error('Failed to load company data: An unexpected error occurred.');
      }
    }
  }

  static async createCompany(companyData: CompanyForm): Promise<Company> {
    try {
      console.log('🚀 Starting company creation process...');
      console.log('📋 Company data:', {
        name: companyData.name,
        industry: companyData.industry,
        companyType: companyData.companyType,
        hasDescription: !!companyData.description,
        hasTagline: !!companyData.tagline,
        hasWebsite: !!companyData.website,
        hasEmail: !!companyData.email,
        isRemoteFriendly: companyData.isRemoteFriendly,
        foundedYear: companyData.foundedYear,
        employeeCount: companyData.employeeCount,
        valuesCount: companyData.values?.length || 0,
        benefitsCount: companyData.benefits?.length || 0,
        perksCount: companyData.perks?.length || 0,
      });

      // Step 1: Check authentication
      console.log('🔐 Checking user authentication...');
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError) {
        console.error('❌ Authentication error:', authError);
        throw new Error(`Authentication failed: ${authError.message}`);
      }
      
      if (!user) {
        console.error('❌ No authenticated user found');
        throw new Error('You must be logged in to create a company. Please sign in and try again.');
      }
      
      console.log('✅ User authenticated:', { userId: user.id, email: user.email });

      // Step 2: Validate required fields
      console.log('🔍 Validating company data...');
      const validationErrors = this.validateCompanyData(companyData);
      if (validationErrors.length > 0) {
        console.error('❌ Validation errors:', validationErrors);
        throw new Error(`Validation failed:\n${validationErrors.join('\n')}`);
      }
      console.log('✅ Company data validation passed');

      // Step 3: Prepare company data for insertion
      console.log('📝 Preparing company data for database insertion...');
      const companyInsertData = {
        name: companyData.name?.trim(),
        description: companyData.description?.trim() || null,
        tagline: companyData.tagline?.trim() || null,
        industry: companyData.industry?.trim() || null,
        company_size: companyData.companySize?.trim() || null,
        company_type: companyData.companyType || 'private',
        website: companyData.website?.trim() || null,
        impressum_url: companyData.impressumUrl?.trim() || null,
        email: companyData.email?.trim() || null,
        phone: companyData.phone?.trim() || null,
        linkedin_url: companyData.linkedinUrl?.trim() || null,
        twitter_url: companyData.twitterUrl?.trim() || null,
        facebook_url: companyData.facebookUrl?.trim() || null,
        instagram_url: companyData.instagramUrl?.trim() || null,
        headquarters_address: companyData.headquartersAddress?.trim() || null,
        headquarters_city: companyData.headquartersCity?.trim() || null,
        headquarters_state: companyData.headquartersState?.trim() || null,
        headquarters_country: companyData.headquartersCountry?.trim() || null,
        headquarters_postal_code: companyData.headquartersPostalCode?.trim() || null,
        is_remote_friendly: companyData.isRemoteFriendly || false,
        remote_policy: companyData.remotePolicy?.trim() || null,
        founded_year: companyData.foundedYear || null,
        revenue_range: companyData.revenueRange?.trim() || null,
        employee_count: companyData.employeeCount || null,
        mission_statement: companyData.missionStatement?.trim() || null,
        values: companyData.values && companyData.values.length > 0 ? companyData.values : null,
        benefits: companyData.benefits && companyData.benefits.length > 0 ? companyData.benefits : null,
        perks: companyData.perks && companyData.perks.length > 0 ? companyData.perks : null,
        brand_primary_color: companyData.brandColors?.primary?.trim() || null,
        brand_secondary_color: companyData.brandColors?.secondary?.trim() || null,
      };

      console.log('📊 Prepared data:', {
        name: companyInsertData.name,
        industry: companyInsertData.industry,
        companyType: companyInsertData.company_type,
        hasDescription: !!companyInsertData.description,
        hasWebsite: !!companyInsertData.website,
        hasEmail: !!companyInsertData.email,
        isRemoteFriendly: companyInsertData.is_remote_friendly,
        foundedYear: companyInsertData.founded_year,
        employeeCount: companyInsertData.employee_count,
        valuesCount: companyInsertData.values?.length || 0,
        benefitsCount: companyInsertData.benefits?.length || 0,
        perksCount: companyInsertData.perks?.length || 0,
      });

      // Step 4: Insert company into database with creator as initial member
      console.log('💾 Inserting company into database...');
      
      // Include the creator's user ID in the member_ids array from the start
      const companyInsertDataWithMembers = {
        ...companyInsertData,
        member_ids: [user.id] // Add creator to member_ids array
      };
      
      const { data, error } = await supabase
        .from('companies')
        .insert(companyInsertDataWithMembers)
        .select()
        .single();

      if (error) {
        console.error('❌ Database insertion error:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          companyData: {
            name: companyInsertData.name,
            industry: companyInsertData.industry,
            companyType: companyInsertData.company_type,
          }
        });

        // Provide detailed error messages based on error code
        let detailedMessage = `Failed to create company: ${error.message}`;
        
        if (error.code === '23505') { // Unique constraint violation
          if (error.message.includes('slug')) {
            detailedMessage = `A company with a similar name already exists. Please choose a different company name or try again later.`;
          } else if (error.message.includes('email')) {
            detailedMessage = `A company with this email address already exists. Please use a different email address.`;
          } else {
            detailedMessage = `A company with this information already exists. Please check your company details and try again.`;
          }
        } else if (error.code === '23514') { // Check constraint violation
          detailedMessage = `Invalid company data: ${error.message}. Please check your input and try again.`;
        } else if (error.code === '23503') { // Foreign key constraint violation
          detailedMessage = `Invalid reference in company data: ${error.message}. Please check your input and try again.`;
        } else if (error.code === '42501') { // Insufficient privilege
          detailedMessage = `You don't have permission to create a company. Please contact support if this issue persists.`;
        } else if (error.code === 'PGRST301') { // Row Level Security violation
          detailedMessage = `Access denied. You may not have permission to create companies. Please contact support.`;
        }

        throw new Error(detailedMessage);
      }

      console.log('✅ Company created successfully:', { 
        companyId: data.id, 
        name: data.name, 
        slug: data.slug,
        memberIds: data.member_ids
      });

      // Step 5: Add creator as admin member in company_members table
      console.log('👤 Adding creator as admin member...');
      try {
        await this.addCompanyMember(data.id, user.id, 'admin', 'Company Creator', 'Management');
        console.log('✅ Creator added as admin member successfully');
      } catch (memberError) {
        console.error('❌ Failed to add creator as admin member:', memberError);
        // Don't throw here - company was created successfully, just member addition failed
        console.warn('⚠️ Company created but failed to add creator as member. You may need to manually add yourself as an admin.');
      }

      console.log('🎉 Company creation process completed successfully!');
      return data as Company;

    } catch (error) {
      console.error('💥 Company creation failed:', error);
      
      // Re-throw with additional context
      if (error instanceof Error) {
        throw new Error(`Company Creation Failed: ${error.message}`);
      } else {
        throw new Error(`Company Creation Failed: An unexpected error occurred. Please try again or contact support if the problem persists.`);
      }
    }
  }

  // Helper function to validate company data
  private static validateCompanyData(companyData: CompanyForm): string[] {
    const errors: string[] = [];

    // Required fields
    if (!companyData.name || companyData.name.trim().length === 0) {
      errors.push('• Company name is required');
    } else if (companyData.name.trim().length < 2) {
      errors.push('• Company name must be at least 2 characters long');
    } else if (companyData.name.trim().length > 255) {
      errors.push('• Company name must be less than 255 characters');
    }

    // Optional field validations
    if (companyData.description && companyData.description.length > 10000) {
      errors.push('• Company description must be less than 10,000 characters');
    }

    if (companyData.tagline && companyData.tagline.length > 500) {
      errors.push('• Company tagline must be less than 500 characters');
    }

    if (companyData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyData.email)) {
      errors.push('• Please enter a valid email address');
    }

    if (companyData.website && !/^https?:\/\/.+/.test(companyData.website)) {
      errors.push('• Website URL must start with http:// or https://');
    }

    if (companyData.foundedYear && (companyData.foundedYear < 1800 || companyData.foundedYear > new Date().getFullYear())) {
      errors.push('• Founded year must be between 1800 and current year');
    }

    if (companyData.employeeCount && (companyData.employeeCount < 1 || companyData.employeeCount > 1000000)) {
      errors.push('• Employee count must be between 1 and 1,000,000');
    }

    if (companyData.phone && !/^[\+]?[0-9\s\-\(\)]{10,}$/.test(companyData.phone)) {
      errors.push('• Please enter a valid phone number');
    }

    return errors;
  }

  static async updateCompany(id: string, companyData: Partial<CompanyForm>): Promise<Company> {
    const { data, error } = await supabase
      .from('companies')
      .update({
        name: companyData.name,
        description: companyData.description,
        tagline: companyData.tagline,
        industry: companyData.industry,
        company_size: companyData.companySize,
        company_type: companyData.companyType,
        website: companyData.website,
        impressum_url: companyData.impressumUrl,
        email: companyData.email,
        phone: companyData.phone,
        linkedin_url: companyData.linkedinUrl,
        twitter_url: companyData.twitterUrl,
        facebook_url: companyData.facebookUrl,
        instagram_url: companyData.instagramUrl,
        logo_url: companyData.logoUrl === '' ? null : companyData.logoUrl,
        cover_image_url: companyData.coverImageUrl === '' ? null : companyData.coverImageUrl,
        headquarters_address: companyData.headquartersAddress,
        headquarters_city: companyData.headquartersCity,
        headquarters_state: companyData.headquartersState,
        headquarters_country: companyData.headquartersCountry,
        headquarters_postal_code: companyData.headquartersPostalCode,
        is_remote_friendly: companyData.isRemoteFriendly,
        remote_policy: companyData.remotePolicy,
        founded_year: companyData.foundedYear,
        revenue_range: companyData.revenueRange,
        employee_count: companyData.employeeCount,
        mission_statement: companyData.missionStatement,
        values: companyData.values,
        benefits: companyData.benefits,
        perks: companyData.perks,
        brand_primary_color: companyData.brandColors?.primary,
        brand_secondary_color: companyData.brandColors?.secondary,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Company;
  }

  static async deleteCompany(id: string): Promise<void> {
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Company member operations
  static async getCompanyMembers(companyId: string): Promise<CompanyMember[]> {
    const { data, error } = await supabase
      .from('company_members')
      .select(`
        *,
        user:users(id, first_name, last_name, email)
      `)
      .eq('company_id', companyId)
      .eq('is_active', true);

    if (error) throw error;
    return data as CompanyMember[];
  }

  static async addCompanyMember(companyId: string, userId: string, role: 'owner' | 'admin' | 'member' | 'viewer', jobTitle?: string, department?: string): Promise<CompanyMember> {
    try {
      console.log('👤 Adding company member:', {
        companyId,
        userId,
        role,
        jobTitle,
        department
      });

      // Use the SQL function to add company member (this will also update the member_ids array)
      const { data, error } = await supabase.rpc('add_company_member', {
        company_uuid: companyId,
        user_uuid: userId,
        member_role: role,
        job_title: jobTitle || null,
        department: department || null
      });

      if (error) {
        console.error('❌ Failed to add company member via SQL function:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          companyId,
          userId,
          role
        });

        let detailedMessage = `Failed to add member: ${error.message}`;
        
        if (error.code === '23505') { // Unique constraint violation
          detailedMessage = `This user is already a member of this company.`;
        } else if (error.code === '23503') { // Foreign key constraint violation
          if (error.message.includes('company_id')) {
            detailedMessage = `Company not found. The company may have been deleted.`;
          } else if (error.message.includes('user_id')) {
            detailedMessage = `User not found. The user may have been deleted.`;
          } else {
            detailedMessage = `Invalid reference: ${error.message}`;
          }
        } else if (error.code === '42501') { // Insufficient privilege
          detailedMessage = `You don't have permission to add members to this company.`;
        } else if (error.code === 'PGRST301') { // Row Level Security violation
          detailedMessage = `Access denied. You may not have permission to manage company members.`;
        }

        throw new Error(detailedMessage);
      }

      console.log('✅ Company member added via SQL function:', data);
      
      // Fetch the created member record
      console.log('🔍 Fetching created member record...');
      const { data: memberData, error: memberError } = await supabase
        .from('company_members')
        .select('*')
        .eq('company_id', companyId)
        .eq('user_id', userId)
        .single();

      if (memberError) {
        console.error('❌ Failed to fetch created member record:', memberError);
        throw new Error(`Member was added but failed to retrieve details: ${memberError.message}`);
      }

      console.log('✅ Member record fetched successfully:', memberData);
      return memberData as CompanyMember;

    } catch (error) {
      console.error('💥 addCompanyMember failed:', error);
      
      if (error instanceof Error) {
        throw new Error(`Failed to add company member: ${error.message}`);
      } else {
        throw new Error(`Failed to add company member: An unexpected error occurred.`);
      }
    }
  }

  static async updateCompanyMember(memberId: string, updates: Partial<CompanyMember>): Promise<CompanyMember> {
    const { data, error } = await supabase
      .from('company_members')
      .update(updates)
      .eq('id', memberId)
      .select()
      .single();

    if (error) throw error;
    return data as CompanyMember;
  }

  static async removeCompanyMember(companyId: string, userId: string): Promise<void> {
    // Use the SQL function to remove company member (this will also update the member_ids array)
    const { error } = await supabase.rpc('remove_company_member', {
      company_uuid: companyId,
      user_uuid: userId
    });

    if (error) throw error;
  }

  static async getUserCompanies(userId: string): Promise<Company[]> {
    try {
      console.log('🔍 getUserCompanies called with userId:', userId);
      
      // First try the company_members table approach
      try {
    const { data, error } = await supabase
      .from('company_members')
      .select(`
        company:companies(*)
      `)
      .eq('user_id', userId)
      .eq('is_active', true);

        console.log('📊 getUserCompanies query result:', { data, error });

        if (error) {
          console.error('❌ getUserCompanies error:', {
            code: error.code,
            message: error.message,
            details: error.details,
            hint: error.hint
          });
          throw error;
        }

        if (!data) {
          console.warn('⚠️ getUserCompanies returned null data');
          return [];
        }

        const companies = data.map((item: any) => item.company).filter(Boolean) as Company[];
        console.log('✅ getUserCompanies returning companies:', companies.length, 'companies');
        return companies;
      } catch (companyMembersError) {
        console.warn('⚠️ company_members table query failed, trying fallback method:', companyMembersError);
        
        // Fallback: Query companies table directly using member_ids array
        console.log('🔄 Trying fallback method with member_ids array...');
        
        // First, let's try to get all companies and filter in JavaScript
        const { data: allCompanies, error: allCompaniesError } = await supabase
          .from('companies')
          .select('*')
          .eq('is_active', true);

        console.log('📊 All companies query result:', { allCompanies, allCompaniesError });

        if (allCompaniesError) {
          console.error('❌ All companies query failed:', {
            code: allCompaniesError.code,
            message: allCompaniesError.message,
            details: allCompaniesError.details,
            hint: allCompaniesError.hint
          });
          throw allCompaniesError;
        }

        if (!allCompanies) {
          console.warn('⚠️ All companies query returned null data');
          return [];
        }

        // Filter companies where user ID is in member_ids array
        const userCompanies = allCompanies.filter(company => 
          company.member_ids && 
          Array.isArray(company.member_ids) && 
          company.member_ids.includes(userId)
        );

        console.log('✅ Fallback method found companies:', userCompanies.length, 'companies for user:', userId);
        return userCompanies as Company[];
      }
    } catch (error) {
      console.error('💥 getUserCompanies error:', error);
      throw error;
    }
  }

  // Debug function to check all companies and their member_ids
  static async debugAllCompanies(): Promise<void> {
    try {
      console.log('🔍 Debugging all companies and their member_ids...');
      
      const { data: companies, error } = await supabase
        .from('companies')
        .select('id, name, member_ids, is_active')
        .limit(10);
      
      if (error) {
        console.error('❌ Error fetching companies:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        });
        return;
      }
      
      console.log('📊 Companies found:', companies?.length || 0);
      companies?.forEach((company, index) => {
        console.log(`Company ${index + 1}:`, {
          id: company.id,
          name: company.name,
          is_active: company.is_active,
          member_ids: company.member_ids,
          member_ids_type: typeof company.member_ids,
          member_ids_length: Array.isArray(company.member_ids) ? company.member_ids.length : 'not array'
        });
      });
    } catch (error) {
      console.error('💥 Error in debugAllCompanies:', error);
    }
  }

  // Debug function to check company members
  static async debugCompanyMembers(companyId: string): Promise<{
    currentUserId: string | null;
    membersCount: number;
    members: any[];
    memberIds: string[];
  }> {
    try {
      console.log('🔍 Debugging company members for company:', companyId);
      
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      console.log('👤 Current auth user:', {
        user: user ? { id: user.id, email: user.email } : null,
        authError
      });

      // Get member_ids from companies table
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('member_ids')
        .eq('id', companyId)
        .single();

      console.log('📊 Company member_ids:', {
        companyData,
        companyError,
        memberIds: companyData?.member_ids
      });

      // Try to get company_members (might fail due to RLS)
      let membersData = [];
      let membersError = null;
      try {
        const { data, error } = await supabase
          .from('company_members')
          .select('*')
          .eq('company_id', companyId);
        
        membersData = data || [];
        membersError = error;
      } catch (err) {
        membersError = err;
        console.log('⚠️ Could not fetch company_members (likely RLS issue):', err);
      }
      
      console.log('📊 Company members table:', {
        data: membersData,
        error: membersError,
        count: membersData?.length || 0
      });

      // Check if user is in member_ids array
      const isInMemberIds = user && companyData?.member_ids?.includes(user.id);
      console.log('🔍 MEMBER_IDS CHECK:', {
        currentUserId: user?.id,
        memberIds: companyData?.member_ids,
        isInMemberIds
      });
      
      return {
        currentUserId: user?.id || null,
        membersCount: membersData?.length || 0,
        members: membersData || [],
        memberIds: companyData?.member_ids || []
      };
      
    } catch (error) {
      console.error('💥 Debug company members error:', error);
      return {
        currentUserId: null,
        membersCount: 0,
        members: [],
        memberIds: []
      };
    }
  }

  // Check if current user is admin of a specific company
  static async isUserAdminOfCompany(companyId: string, userId?: string): Promise<boolean> {
    try {
      // If no userId provided, get current user
      if (!userId) {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          console.error('❌ Auth error or no user:', authError);
          return false;
        }
        userId = user.id;
        console.log('🔐 Current user ID from auth:', userId);
      }

      console.log('🔍 Checking admin status using member_ids array:', {
        companyId,
        userId,
        timestamp: new Date().toISOString()
      });

      // Get the company's member_ids array directly from the companies table
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('member_ids')
        .eq('id', companyId)
        .single();

      console.log('📊 Company member_ids query result:', {
        companyData,
        companyError,
        memberIds: companyData?.member_ids
      });

      if (companyError) {
        console.error('❌ Database error fetching company member_ids:', companyError);
        return false;
      }

      if (!companyData || !companyData.member_ids) {
        console.log('⚠️ No member_ids found for company');
        return false;
      }

      // Check if current user ID is in the member_ids array
      const isMember = companyData.member_ids.includes(userId);
      
      console.log('✅ Member check result:', {
        userId,
        memberIds: companyData.member_ids,
        isMember,
        isAdmin: isMember // For now, if you're in member_ids, you're considered an admin
      });

      // For now, if the user is in the member_ids array, they're considered an admin
      // Later we can enhance this to check the actual role from company_members table
      return isMember;
    } catch (error) {
      console.error('💥 Error checking user admin status:', error);
      return false;
    }
  }

  // Company jobs operations
  static async getCompanyJobs(companyId: string, filters?: {
    jobType?: string;
    employmentType?: string;
    experienceLevel?: string;
    isRemote?: boolean;
    location?: string;
    search?: string;
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<CompanyJob[]> {
    let query = supabase
      .from('company_jobs')
      .select('*')
      .eq('company_id', companyId);

    if (filters?.jobType) {
      query = query.eq('job_type', filters.jobType);
    }

    if (filters?.employmentType) {
      query = query.eq('employment_type', filters.employmentType);
    }

    if (filters?.experienceLevel) {
      query = query.eq('experience_level', filters.experienceLevel);
    }

    if (filters?.isRemote !== undefined) {
      query = query.eq('is_remote', filters.isRemote);
    }

    if (filters?.location) {
      query = query.or(`location.ilike.%${filters.location}%,city.ilike.%${filters.location}%,country.ilike.%${filters.location}%`);
    }

    if (filters?.search) {
      query = query.textSearch('title,description,requirements', filters.search);
    }

    if (filters?.isActive !== undefined) {
      query = query.eq('is_active', filters.isActive);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, (filters.offset + (filters.limit || 10)) - 1);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data as CompanyJob[];
  }

  static async getAllJobs(filters?: {
    companyId?: string;
    jobType?: string;
    employmentType?: string;
    experienceLevel?: string;
    isRemote?: boolean;
    location?: string;
    search?: string;
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<CompanyJob[]> {
    let query = supabase
      .from('company_jobs')
      .select(`
        *,
        company:companies(*)
      `)
      .eq('is_active', true);

    if (filters?.companyId) {
      query = query.eq('company_id', filters.companyId);
    }

    if (filters?.jobType) {
      query = query.eq('job_type', filters.jobType);
    }

    if (filters?.employmentType) {
      query = query.eq('employment_type', filters.employmentType);
    }

    if (filters?.experienceLevel) {
      query = query.eq('experience_level', filters.experienceLevel);
    }

    if (filters?.isRemote !== undefined) {
      query = query.eq('is_remote', filters.isRemote);
    }

    if (filters?.location) {
      query = query.or(`location.ilike.%${filters.location}%,city.ilike.%${filters.location}%,country.ilike.%${filters.location}%`);
    }

    if (filters?.search) {
      query = query.textSearch('title,description,requirements', filters.search);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    if (filters?.offset) {
      query = query.range(filters.offset, (filters.offset + (filters.limit || 10)) - 1);
    }

    const { data, error } = await query.order('created_at', { ascending: false });

    if (error) throw error;
    return data as CompanyJob[];
  }

  static async getJobById(id: string): Promise<CompanyJob | null> {
    console.log('CompanyService.getJobById called with id:', id);
    const { data, error } = await supabase
      .from('company_jobs')
      .select(`
        *,
        company:companies(*)
      `)
      .eq('id', id)
      .eq('is_active', true)
      .single();

    console.log('getJobById result - data:', data, 'error:', error);
    if (error) {
      console.error('getJobById error:', error);
      return null;
    }
    return data as CompanyJob;
  }

  static async createJob(companyId: string, jobData: JobForm): Promise<CompanyJob> {
    const { data, error } = await supabase
      .from('company_jobs')
      .insert({
        company_id: companyId,
        title: jobData.title,
        description: jobData.description,
        requirements: jobData.requirements,
        responsibilities: jobData.responsibilities,
        benefits: jobData.benefits,
        job_type: jobData.jobType,
        employment_type: jobData.employmentType,
        experience_level: jobData.experienceLevel,
        salary_min: jobData.salaryMin,
        salary_max: jobData.salaryMax,
        salary_currency: jobData.salaryCurrency,
        salary_period: jobData.salaryPeriod,
        is_remote: jobData.isRemote,
        location: jobData.location,
        city: jobData.city,
        state: jobData.state,
        country: jobData.country,
        postal_code: jobData.postalCode,
        skills_required: jobData.skillsRequired,
        skills_preferred: jobData.skillsPreferred,
        education_required: jobData.educationRequired,
        experience_years: jobData.experienceYears,
        application_deadline: jobData.applicationDeadline,
        start_date: jobData.startDate,
        is_active: jobData.isActive,
        is_featured: jobData.isFeatured,
      })
      .select()
      .single();

    if (error) throw error;
    return data as CompanyJob;
  }

  static async updateJob(id: string, jobData: Partial<JobForm>): Promise<CompanyJob> {
    const { data, error } = await supabase
      .from('company_jobs')
      .update({
        title: jobData.title,
        description: jobData.description,
        requirements: jobData.requirements,
        responsibilities: jobData.responsibilities,
        benefits: jobData.benefits,
        job_type: jobData.jobType,
        employment_type: jobData.employmentType,
        experience_level: jobData.experienceLevel,
        salary_min: jobData.salaryMin,
        salary_max: jobData.salaryMax,
        salary_currency: jobData.salaryCurrency,
        salary_period: jobData.salaryPeriod,
        is_remote: jobData.isRemote,
        location: jobData.location,
        city: jobData.city,
        state: jobData.state,
        country: jobData.country,
        postal_code: jobData.postalCode,
        skills_required: jobData.skillsRequired,
        skills_preferred: jobData.skillsPreferred,
        education_required: jobData.educationRequired,
        experience_years: jobData.experienceYears,
        application_deadline: jobData.applicationDeadline,
        start_date: jobData.startDate,
        is_active: jobData.isActive,
        is_featured: jobData.isFeatured,
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as CompanyJob;
  }

  static async deleteJob(id: string): Promise<void> {
    const { error } = await supabase
      .from('company_jobs')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Analytics operations
  static async incrementProfileViews(companyId: string): Promise<void> {
    const { error } = await supabase.rpc('increment_company_profile_views', {
      company_id: companyId
    });

    if (error) throw error;
  }

  static async incrementJobViews(jobId: string): Promise<void> {
    const { error } = await supabase.rpc('increment_job_views', {
      job_id: jobId
    });

    if (error) throw error;
  }

  static async incrementApplicationsReceived(companyId: string): Promise<void> {
    const { error } = await supabase.rpc('increment_company_applications', {
      company_id: companyId
    });

    if (error) throw error;
  }
}
